import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { useAppDispatch } from "app/hooks";
import ScheduleNextAppointmentModal from "components/schedule-next-appointment-modal";
import { EVENT_TYPE_APPOINTMENT } from "constants/appointments";
import { SCHEDULE_VIEW_IN_PATIENT_HEADER } from "constants/feature-flags";
import { AppointmentType } from "services/types/appointment-types.type";
import { Appointment } from "services/types/appointments.type";
import { Patient } from "services/types/patients.type";

import useScheduleNextAppointmentPrompt from "./use-schedule-next-appointment-prompt";

import {
  openForm,
  startFollowUpScheduling,
  startPatientCreateAppointmentMode
} from "../features/scheduling/scheduler-slice";

type SetupAppointmentModalFn = (
  appointment: Partial<Appointment>,
  actionType: "add" | "update" | "delete"
) => void;

type ScheduleNextAppointmentProviderProps = {
  children?: React.ReactNode;
  setupAppointmentModal: SetupAppointmentModalFn;
  appointmentFormId: string;
};

interface ScheduleNextAppointmentContextValue {
  isModalOpen: boolean;
  completedAppointment: Appointment | null;
  initScheduleNextAppointment: (
    appointment: Appointment,
    onScheduled?: () => void
  ) => Promise<void>;
  closeModal: () => void;
}

const ScheduleNextAppointmentContext =
  React.createContext<ScheduleNextAppointmentContextValue | null>(null);

export function ScheduleNextAppointmentProvider({
  children,
  setupAppointmentModal,
  appointmentFormId
}: ScheduleNextAppointmentProviderProps) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const flags = useFlags();
  const isCalendarModeSupported = flags[SCHEDULE_VIEW_IN_PATIENT_HEADER];
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [completedAppointment, setCompletedAppointment] =
    useState<Appointment | null>(null);
  const [onScheduledCallback, setOnScheduledCallback] = useState<
    (() => void) | undefined
  >(undefined);

  const {
    appointmentTypes,
    getPromptAppointment,
    getSuggestedAppointmentType
  } = useScheduleNextAppointmentPrompt();

  const initScheduleNextAppointment = useCallback(
    async (appointment: Appointment, onScheduled?: () => void) => {
      const promptAppointment = await getPromptAppointment(appointment);
      if (!promptAppointment) return;

      setCompletedAppointment(promptAppointment as Appointment);
      setIsModalOpen(true);
      if (onScheduled) {
        setOnScheduledCallback(() => onScheduled);
      }
    },
    [getPromptAppointment]
  );

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setCompletedAppointment(null);
    setOnScheduledCallback(undefined);
  }, []);

  const handleScheduleNow = useCallback(
    (appointmentTypeId: number) => {
      if (!completedAppointment) return;

      const selectedType = appointmentTypes.find(
        (type) => type.id === appointmentTypeId
      );
      const patientForScheduling = completedAppointment.patient;

      if (isCalendarModeSupported) {
        dispatch(
          startPatientCreateAppointmentMode({
            patient: patientForScheduling,
            returnToPath: location.pathname + location.search
          })
        );
        dispatch(
          startFollowUpScheduling({
            completedAppointmentId: completedAppointment.id,
            patient: patientForScheduling,
            appointmentType: selectedType || null
          })
        );
      } else {
        // Fallback when feature flag is off: open form directly (slot-click wouldn't use patient).
        dispatch(
          startFollowUpScheduling({
            completedAppointmentId: completedAppointment.id,
            patient: patientForScheduling,
            appointmentType: selectedType || null
          })
        );
        setupAppointmentModal(
          {
            type: EVENT_TYPE_APPOINTMENT,
            patient: patientForScheduling,
            clinic: completedAppointment.clinic,
            appointment_type: selectedType || null
          } as Partial<Appointment>,
          "add"
        );
        dispatch(openForm(appointmentFormId));
      }

      setIsModalOpen(false);
      setCompletedAppointment(null);

      if (onScheduledCallback) {
        onScheduledCallback();
      }
      setOnScheduledCallback(undefined);
    },
    [
      completedAppointment,
      appointmentTypes,
      dispatch,
      isCalendarModeSupported,
      location.pathname,
      location.search,
      onScheduledCallback,
      setupAppointmentModal,
      appointmentFormId
    ]
  );

  // Get the suggested appointment type from the completed appointment
  const suggestedAppointmentType: AppointmentType | null = React.useMemo(
    () => getSuggestedAppointmentType(completedAppointment),
    [completedAppointment, getSuggestedAppointmentType]
  );

  const patient: Patient | null = completedAppointment?.patient || null;

  const contextValue = useMemo(
    () => ({
      isModalOpen,
      completedAppointment,
      initScheduleNextAppointment,
      closeModal
    }),
    [isModalOpen, completedAppointment, initScheduleNextAppointment, closeModal]
  );

  return (
    <ScheduleNextAppointmentContext.Provider value={contextValue}>
      {children}

      <ScheduleNextAppointmentModal
        open={isModalOpen}
        onClose={closeModal}
        onScheduleNow={handleScheduleNow}
        patient={patient}
        suggestedAppointmentType={suggestedAppointmentType}
      />
    </ScheduleNextAppointmentContext.Provider>
  );
}

export default function useScheduleNextAppointment() {
  const context = React.useContext(ScheduleNextAppointmentContext);
  if (!context) {
    throw new Error(
      `useScheduleNextAppointment must be used within a ScheduleNextAppointmentProvider`
    );
  }

  return context;
}
